import type { ComponentPropsWithoutRef } from 'react';
import { useEffect, useRef } from 'react';

import usePrevious from '@proton/hooks/usePrevious';
import { scrollIntoView } from '@proton/shared/lib/helpers/dom';
import clsx from '@proton/utils/clsx';

import DropdownMenuButton from '../dropdown/DropdownMenuButton';

export interface OptionProps<V> extends Omit<ComponentPropsWithoutRef<'button'>, 'value' | 'onChange' | 'title'> {
    value: V;
    onChange?: (value: V) => void;
    selected?: boolean;
    active?: boolean;
    /**
     * Truncates and adds an ellipsis if the text exceeds the width of the dropdown.
     */
    truncate?: boolean;
    title: string;
    disableFocusOnActive?: boolean;
    searchStrings?: string[];
    preventScroll?: boolean;
    /**
     * Class name applied to the wrapper of the option.
     */
    optionWrapperClassName?: string;
}

const Option = <V,>({
    type = 'button',
    value,
    selected,
    active,
    truncate,
    onChange,
    title,
    children = title,
    disableFocusOnActive,
    searchStrings,
    className,
    preventScroll = false,
    optionWrapperClassName = '',
    ...rest
}: OptionProps<V>) => {
    const ref = useRef<HTMLButtonElement | null>(null);
    const previousActive = usePrevious(active);

    useEffect(() => {
        if (!previousActive && active) {
            if (!disableFocusOnActive) {
                ref.current?.focus({ preventScroll });
            } else {
                scrollIntoView(ref.current, { block: 'center' });
            }
        }
    }, [active]);

    const handleClick = () => {
        onChange?.(value);
    };

    return (
        <li className={clsx(['dropdown-item', optionWrapperClassName])}>
            <DropdownMenuButton
                ref={ref}
                type={type}
                isSelected={selected}
                onClick={handleClick}
                title={title}
                className={clsx([
                    className,
                    'block w-full text-left',
                    active && 'active',
                    truncate ? 'text-ellipsis' : 'text-break',
                ])}
                {...rest}
            >
                {children}
            </DropdownMenuButton>
        </li>
    );
};

export default Option;
